import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'preact/hooks'
import { api } from '../api/client'
import type { ImportReport } from '../api/generated/ImportReport'
import type { Position } from '../api/generated/Position'
import type { Shift } from '../api/generated/Shift'
import type { VolunteerImport } from '../api/generated/VolunteerImport'
import { useAction } from '../api/useAction'
import { useApi } from '../api/useApi'
import { decodeCsv } from './csv'
import {
  isValid,
  type ParsedRow,
  plural,
  previewSummary,
  readVolunteers,
  shiftReference,
  withReport,
} from './rows'

const messages = { invalid_import: '1000 bénévoles au plus par import.' }

function example(positions: Position[] | undefined, shifts: Shift[] | undefined): string {
  const shift = shifts?.[0]
  const position = positions?.find((p) => p.id === shift?.position_id)
  return shift && position ? shiftReference(shift, position) : 'Accueil 06/02 08:00'
}

function Preview({ rows }: { rows: ParsedRow[] }) {
  const valid = rows.filter(isValid)
  const invalid = rows.filter((row) => !isValid(row))
  return (
    <Stack spacing={1}>
      <Alert severity={valid.length > 0 ? 'info' : 'warning'}>{previewSummary(rows)}</Alert>
      <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
        <List dense disablePadding>
          {invalid.map(({ line, row, issues }) => (
            <ListItem key={line} disableGutters>
              <ListItemText
                primary={`Ligne ${line} : ${issues.join(' ; ')}`}
                secondary={[row.name, row.email || row.phone].filter(Boolean).join(' · ')}
                slotProps={{ primary: { color: 'error' } }}
              />
            </ListItem>
          ))}
          {valid.map(({ line, row }) => (
            <ListItem key={line} disableGutters>
              <ListItemText
                primary={row.name || row.email || row.phone}
                secondary={[
                  row.name && (row.email || row.phone),
                  row.shift_ids.length > 0 && plural(row.shift_ids.length, 'créneau', 'créneaux'),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Stack>
  )
}

/** Imports a list of volunteers, checked by the server before anything is imported. */
export function ImportDialog({ slug, onClose }: { slug: string; onClose: () => void }) {
  const positions = useApi<Position[]>(`/events/${slug}/positions`)
  const shifts = useApi<Shift[]>(`/events/${slug}/shifts`)
  const [text, setText] = useState('')
  const [rows, setRows] = useState<ParsedRow[]>()
  const [problem, setProblem] = useState<string>()
  const [imported, setImported] = useState<number>()
  const { error, pending, run } = useAction(messages)
  const path = `/events/${slug}/volunteers/import`

  const edit = (value: string) => {
    setText(value)
    setRows(undefined)
    setProblem(undefined)
  }
  const pick = async (e: Event) => {
    const input = e.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = ''
    if (file) edit(decodeCsv(await file.arrayBuffer()))
  }
  const send = async (sent: ParsedRow[], dry_run: boolean) => {
    const body: VolunteerImport = { rows: sent.map((r) => r.row), dry_run }
    return api.post<ImportReport>(path, body)
  }
  const check = () =>
    run(async () => {
      const parsed = readVolunteers(text, positions.data ?? [], shifts.data ?? [])
      if ('error' in parsed) {
        setProblem(parsed.error)
        return
      }
      const checked = parsed.rows.filter(isValid)
      const report = checked.length > 0 ? await send(checked, true) : { errors: [], imported: 0 }
      setRows(withReport(parsed.rows, checked, report))
    })
  const submit = (valid: ParsedRow[]) =>
    run(async () => {
      const report = await send(valid, false)
      if (report.errors.length > 0) setRows(withReport(rows ?? [], valid, report))
      else setImported(report.imported)
    })

  const valid = rows?.filter(isValid) ?? []
  const loading = !positions.data || !shifts.data
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Importer des bénévoles</DialogTitle>
      <DialogContent>
        {imported !== undefined ? (
          <Alert severity="success">
            {plural(imported, 'bénévole importé', 'bénévoles importés')}.
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Copiez les cellules d'un tableur, ou choisissez un fichier CSV (virgule ou
              point-virgule). Première ligne : les en-têtes « Nom », « Email », « Téléphone », puis
              une ligne par bénévole. L'email est obligatoire : il retrouve le compte, ou le crée.
              Le téléphone, facultatif, reste visible des organisateurs pour appeler le bénévole.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pour inscrire sur des créneaux, une colonne par créneau (« Créneau », « Créneau 2 »…),
              sous la forme « {example(positions.data, shifts.data)} » : poste, jour, heure de
              début.
            </Typography>
            <TextField
              label="Liste des bénévoles"
              multiline
              minRows={4}
              maxRows={10}
              value={text}
              onInput={(e) => edit((e.target as HTMLTextAreaElement).value)}
              slotProps={{ htmlInput: { spellCheck: false } }}
            />
            <Button component="label" variant="outlined" sx={{ alignSelf: 'flex-start' }}>
              Choisir un fichier
              <input hidden type="file" accept=".csv,.tsv,.txt,text/csv" onChange={pick} />
            </Button>
            {(positions.error || shifts.error) && (
              <Alert severity="error">{positions.error ?? shifts.error}</Alert>
            )}
            {problem && <Alert severity="error">{problem}</Alert>}
            {rows && <Preview rows={rows} />}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{imported === undefined ? 'Annuler' : 'Fermer'}</Button>
        {imported === undefined &&
          (rows ? (
            <Button
              variant="contained"
              loading={pending}
              disabled={valid.length === 0}
              onClick={() => submit(valid)}
            >
              Importer {plural(valid.length, 'bénévole', 'bénévoles')}
            </Button>
          ) : (
            <Button
              variant="contained"
              loading={pending}
              disabled={loading || text.trim() === ''}
              onClick={check}
            >
              Vérifier
            </Button>
          ))}
      </DialogActions>
    </Dialog>
  )
}
